import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ChartOfAccountsSearch from "../ChartOfAccountsSearch";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";

// The GL accounts this client's withholding rows and return payments post to (G1, G2). Anything not
// chosen uses the default shown under the field. Pick-only, postable
// accounts only (ChartOfAccountsSearch postableOnly + strict).

export const POSTING_FIELDS = [
  ["cashAccount", "Cash", "Credited for EWT rows paid in cash; debited for withholding-only cash rows."],
  ["apAccount", "Accounts payable", "Used instead of cash when a row's credit account is AP."],
  ["ewtPayableAccount", "EWT payable (2300)", "Credited with the expanded withholding tax."],
  ["compPayableAccount", "Compensation WHT payable (2310)", "Credited with tax withheld on compensation."],
  ["compDebitAccount", "Compensation debit", "Debited for compensation rows — salaries payable or cash."],
  ["penaltyExpenseAccount", "Penalties expense", "Debited with return penalties when a return payment is posted."],
  ["refundReceivableAccount", "Refund receivable", "1601-EQ over-remittance to be refunded by the BIR. No default — choose one if you use refunds."],
  ["tccAccount", "Tax credit certificates", "1601-EQ over-remittance issued as a TCC. No default — choose one if you use TCCs."],
];

const label = (a) => (a ? `${a.accountNumber} - ${a.accountName}` : "");

const PostingAccountsPanel = ({ client, onClose, onSaved }) => {
  const clientId = client?._id;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState({}); // field → account | null (null = use the default)
  const [fieldErrors, setFieldErrors] = useState({});

  const load = async () => {
    try {
      setData((await withholdingTaxAPI.getPostingAccounts(clientId)).data);
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A field's current pick: the local change, else the saved account.
  const current = (f) => (f in picked ? picked[f] : (data?.resolved[f]?.source === "saved" ? data.resolved[f] : null));

  const save = async () => {
    setFieldErrors({});
    const body = Object.fromEntries(Object.entries(picked).map(([f, a]) => [f, a ? a._id : null]));
    try {
      const res = await withholdingTaxAPI.savePostingAccounts(clientId, body);
      setData(res.data);
      setPicked({});
      notify.success("Saved", "Posting accounts saved.");
      onSaved?.(res.data);
    } catch (e) {
      setFieldErrors(e.lineErrors?.[0]?.fields || {});
      notify.error("Couldn't save", e.message);
    }
  };
  const { mutate: runSave, isPending: saving } = useGuardedMutation(save);

  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="posting-accounts-title" className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 id="posting-accounts-title" className="text-lg font-medium text-ink">Posting accounts</h2>
        <p className="text-sm text-ink-body mt-1">{client?.name} — where withholding rows post in the GL. Leave a field empty to use the default.</p>
        {error && <p role="alert" className="text-sm text-danger-500 mt-3">{error}</p>}
        {!data && !error && <p className="text-sm text-ink-body mt-4">Loading…</p>}
        {data && (
          <div className="mt-4 space-y-4">
            {POSTING_FIELDS.map(([f, name, help]) => {
              const resolved = data.resolved[f];
              const pick = current(f);
              return (
                <div key={f} data-field={f}>
                  <label htmlFor={`posting-${f}`} className="block text-sm font-medium text-ink">{name}</label>
                  <p className="text-xs text-ink-body mb-1">{help}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <ChartOfAccountsSearch
                        clientId={clientId}
                        postableOnly
                        strict
                        keyboardSelect
                        selectedOption={pick}
                        onSelect={(a) => setPicked((p) => ({ ...p, [f]: a || null }))}
                        placeholder={resolved ? `Default: ${label(resolved)}` : "Search postable accounts"}
                        showLinkedBadge={false}
                        inputId={`posting-${f}`}
                        ariaLabel={name}
                        invalid={Boolean(fieldErrors[f])}
                      />
                    </div>
                    {pick && <Button variant="ghost" size="sm" onClick={() => setPicked((p) => ({ ...p, [f]: null }))}>Use default</Button>}
                  </div>
                  <p className="text-xs mt-1" data-testid={`resolved-${f}`}>
                    {resolved
                      ? <span className="text-ink-body">{resolved.source === "saved" ? "Saved" : "Default"}: {label(resolved)}</span>
                      : <span className="text-warning-700">No default found — choose an account</span>}
                  </p>
                  {fieldErrors[f] && <p role="alert" className="text-xs text-danger-500 mt-0.5">{fieldErrors[f]}</p>}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={() => runSave()} loading={saving} disabled={!data || !Object.keys(picked).length || saving}>Save</Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PostingAccountsPanel;
